import React, { ButtonHTMLAttributes } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-danger' | 'danger' | 'ghost' | 'link' | 'success' | 'info' | 'neutral';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon';
  fullWidth?: boolean;
  isLoading?: boolean;
  outline?: boolean;
  icon?: string | React.ReactNode;
  as?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'primary', size = 'md', fullWidth, isLoading, outline, icon, as: _as, children, ...props }, ref) => {
    const baseStyles = `inline-flex items-center justify-center transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:pointer-events-none rounded-md ${fullWidth ? 'w-full' : ''}`;

    const getVariantStyles = () => {
      if (variant === 'outline' || variant === 'outline-danger' || outline) {
        switch (variant) {
          case 'outline-danger':
          case 'danger':
            return 'border border-rose-500 bg-white text-rose-500 hover:bg-rose-50 shadow-none';
          case 'primary':
            return 'border border-emerald-700 bg-white text-emerald-700 hover:bg-emerald-50 shadow-none';
          case 'success':
            return 'border border-emerald-500 bg-white text-emerald-500 hover:bg-emerald-50 shadow-none';
          case 'info':
            return 'border border-sky-500 bg-white text-sky-500 hover:bg-sky-50 shadow-none';
          default:
            return 'border border-slate-200 bg-white/70 backdrop-blur-sm hover:bg-slate-50 text-slate-700 shadow-sm';
        }
      }

      switch (variant) {
        case 'primary':
          return 'bg-emerald-700 text-white hover:bg-emerald-800 active:scale-95';
        case 'secondary':
          return 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 active:scale-95';
        case 'danger':
          return 'bg-rose-600 text-white hover:bg-rose-700 active:scale-95';
        case 'ghost':
          return 'bg-transparent shadow-none hover:bg-slate-100 text-slate-500 hover:text-slate-900';
        case 'link':
          return 'bg-transparent text-emerald-700 hover:underline px-0 h-auto shadow-none rounded-none';
        case 'success':
          return 'bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95';
        case 'info':
          return 'bg-sky-500 text-white hover:bg-sky-600 active:scale-95';
        case 'neutral':
          return 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200';
        default:
          return 'bg-emerald-700 text-white hover:bg-emerald-800 active:scale-95';
      }
    };

    const sizes = {
      xs: 'h-7 px-3 text-xs',
      sm: 'h-8 px-4 text-xs',
      md: 'h-10 px-6 sm:text-sm text-xs',
      lg: 'h-12 px-8 sm:text-sm text-xs',
      icon: 'h-10 w-10 p-0',
    };

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${getVariantStyles()} ${sizes[size]} ${className}`}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {icon && (
          <span className="mr-2 inline-flex items-center">
            {typeof icon === 'string' ? <i className={icon} /> : icon}
          </span>
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
